import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Skeleton } from '@mantine/core';

import { TrackableStatusWidget } from 'types/dashboard_widget';
import { TrackableItem, TrackableList } from 'types/trackables';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useDashboard } from 'dashboards/hooks/useDashboard';
import { useModal } from 'hooks/useModal';
import { useTrackableLists, useTrackableNames } from 'trackables/hooks';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';

import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';
import { TrackableItemSelect } from 'trackables/components/TrackableItemSelect/TrackableItemSelect';
import { TrackableListSelect } from 'trackables/components/TrackableListSelect/TrackableListSelect';

import { WidgetFormFooter } from './Footer';
import { WidgetFormHeader } from './Header';

type TrackableStatusFormValues = {
  name: string;
  trackable_lists: TrackableList[];
  trackable_items: TrackableItem[];
};

type TrackableStatusFormProps = {
  widget?: TrackableStatusWidget;
  dashboardId: string;
};

export function TrackableStatusForm({
  widget,
  dashboardId,
}: TrackableStatusFormProps) {
  const { close } = useModal();
  const { data: dashboard } = useDashboard(dashboardId);

  const selectedTrackableListIds = widget?.options?.trackable_list_ids ?? [];
  const selectedTrackableItemIds =
    widget?.options.trackable_item_ids?.filter(Boolean) ?? [];

  const {
    data: defaultTrackableListsData,
    isInitialLoading: isLoadingDefaultTrackableLists,
  } = useTrackableLists({
    params: {
      trackable_list_ids: selectedTrackableListIds.length
        ? selectedTrackableListIds
        : undefined,
      limit: selectedTrackableListIds.length,
    },
    enabled: selectedTrackableListIds.length > 0,
  });

  const defaultTrackableLists = defaultTrackableListsData?.data ?? [];

  const trackableNamesQuery = useTrackableNames(selectedTrackableItemIds);
  const defaultTrackableItems = React.useMemo(
    () =>
      selectedTrackableItemIds.length
        ? selectedTrackableItemIds
            .map((id) => trackableNamesQuery.data?.items[id])
            .filter((item): item is TrackableItem => Boolean(item))
        : [],
    [selectedTrackableItemIds, trackableNamesQuery.data]
  );

  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    register,
    setValue,
    getValues,
    watch,
  } = useForm<TrackableStatusFormValues>({
    defaultValues: {
      name: widget ? widget.name : '',
      // none of them are potentially loaded yet, this will get updated in the
      // useEffects below – so we can just initialize them to empty arrays
      trackable_lists: [],
      trackable_items: [],
    },
  });

  const watchTrackableLists = watch('trackable_lists');
  const hasInitializedDefaultLists = React.useRef(false);
  const hasInitializedDefaultItems = React.useRef(false);

  React.useEffect(() => {
    if (
      !selectedTrackableListIds.length ||
      hasInitializedDefaultLists.current ||
      !defaultTrackableLists.length
    ) {
      return;
    }

    setValue('trackable_lists', defaultTrackableLists, {
      shouldDirty: false,
    });
    hasInitializedDefaultLists.current = true;
  }, [defaultTrackableLists, selectedTrackableListIds, setValue]);

  React.useEffect(() => {
    if (
      !selectedTrackableItemIds.length ||
      hasInitializedDefaultItems.current
    ) {
      return;
    }

    // Wait for both items data to load AND lists to be set before initializing items
    // This prevents the filtering effect from clearing items prematurely
    const listsAreReady =
      selectedTrackableListIds.length === 0 ||
      (watchTrackableLists && watchTrackableLists.length > 0);

    if (
      trackableNamesQuery.isSuccess &&
      defaultTrackableItems.length > 0 &&
      listsAreReady
    ) {
      setValue('trackable_items', defaultTrackableItems, {
        shouldDirty: false,
      });
      hasInitializedDefaultItems.current = true;
    }
  }, [
    defaultTrackableItems,
    selectedTrackableItemIds,
    selectedTrackableListIds.length,
    setValue,
    trackableNamesQuery.isSuccess,
    watchTrackableLists,
  ]);

  React.useEffect(() => {
    const currentItems = getValues('trackable_items') ?? [];
    const lists = watchTrackableLists ?? [];

    // Don't clear items during initial load - only filter when user makes changes
    if (!lists.length) {
      if (currentItems.length && hasInitializedDefaultLists.current) {
        setValue('trackable_items', [], { shouldDirty: true });
      }
      return;
    }

    const allowedListIds = new Set(lists.map(({ id }) => id));
    const filteredItems = currentItems.filter((item) =>
      allowedListIds.has(item.trackable_list_id)
    );

    if (filteredItems.length !== currentItems.length) {
      setValue('trackable_items', filteredItems, { shouldDirty: true });
    }
  }, [getValues, setValue, watchTrackableLists, hasInitializedDefaultLists]);

  const { mutate: updateDashboard, isLoading: isUpdating } = useUpdateDashboard(
    {
      onSuccess: () => close(),
    }
  );

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  function onSubmit({
    name,
    trackable_lists,
    trackable_items,
  }: TrackableStatusFormValues) {
    if (!dashboard || !trackable_lists.length) {
      return;
    }

    const trackableListIds = trackable_lists.map(({ id }) => id);
    const trackableItemIds = trackable_items.map(({ id }) => id);

    const id = widget ? widget.id : crypto.randomUUID();
    const layout = widget
      ? widget.layout
      : getNewWidgetLayout(dashboard.configuration.widgets, 4, 6);

    const widgetOptions: TrackableStatusWidget['options'] = {
      trackable_list_ids: trackableListIds,
    };

    if (trackableItemIds.length) {
      widgetOptions.trackable_item_ids = trackableItemIds;
    }

    const updatedDashboard = upsertWidget(
      dashboard,
      new TrackableStatusWidget(id, name, widgetOptions, layout)
    );

    updateDashboard({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="trackable-status" />

      <Field label="Widget name" error={errors.name} required>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
        />
      </Field>

      <Field
        label="Watchlists"
        error={errors.trackable_lists?.message}
        required
      >
        <Controller
          name="trackable_lists"
          control={control}
          rules={{
            validate: (value) =>
              value && value.length
                ? true
                : 'Please select at least one watchlist.',
          }}
          render={({ field: { onChange, value } }) => {
            if (
              selectedTrackableListIds.length &&
              isLoadingDefaultTrackableLists
            ) {
              return <Skeleton height="2rem" width="100%" />;
            }

            return (
              <TrackableListSelect<true>
                value={value ?? []}
                onChange={(options) => onChange(options ?? [])}
                isClearable
                isMulti
              />
            );
          }}
        />
      </Field>

      <Field label="Watchlist items" error={errors.trackable_items}>
        <Controller
          name="trackable_items"
          control={control}
          render={({ field: { onChange, value } }) => {
            const isLoadingItems =
              selectedTrackableItemIds.length &&
              trackableNamesQuery.isInitialLoading;
            const isLoadingLists =
              selectedTrackableListIds.length && isLoadingDefaultTrackableLists;

            if (isLoadingItems || isLoadingLists) {
              return <Skeleton height="2rem" width="100%" />;
            }

            return (
              <TrackableItemSelect<true>
                trackableLists={watchTrackableLists ?? []}
                value={value ?? []}
                onChange={(options) =>
                  onChange(Array.isArray(options) ? options : [])
                }
                isMulti
                isClearable
              />
            );
          }}
        />
      </Field>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating} />
    </form>
  );
}
